import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';

const AssignmentSubmissionsAuditor = ({ userData }) => {
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('token');

    fetch('/api/assignmentsubmissions/auditor', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch assignment submission conflicts');
        return res.json();
      })
      .then(data => {
        setConflicts(data.conflicts || []);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <div style={{ color: 'red', padding: 24 }}>{error}</div>;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      <h1 style={{ color: '#1D4E89', marginBottom: 8 }}>Assignment Submissions Auditor</h1>
      <p style={{ color: '#666', marginBottom: 24, fontSize: 15 }}>
        Submissions where <code>assignment.questions</code> does not match <code>submissiondata</code> exactly:
        <strong> count mismatch</strong> (different # of questions), <strong>id mismatch</strong> (different question _ids),
        or <strong>definition mismatch</strong> (label, type, maxPoints, choices, answer, etc. differ). Response data is excluded from comparison.
      </p>

      {/* Summary Card */}
      <div style={{
        background: conflicts.length > 0 ? '#fff3e0' : '#e8f5e9',
        border: `2px solid ${conflicts.length > 0 ? '#ff9800' : '#4caf50'}`,
        borderRadius: 8,
        padding: 16,
        marginBottom: 24,
        color: conflicts.length > 0 ? '#e65100' : '#2e7d32'
      }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
          {conflicts.length > 0 ? '⚠️ Conflicts Found' : '✓ No Conflicts'}
        </div>
        <div style={{ fontSize: 24, fontWeight: 700 }}>
          {conflicts.length > 0 ? `${conflicts.length} submission(s) with conflicts` : 'All submissions match their assignments'}
        </div>
        <div style={{ fontSize: 14, marginTop: 4 }}>
          {conflicts.length > 0
            ? 'Assignment was likely edited after submission. Questions may not match responses.'
            : 'Every submission\'s submissiondata matches its assignment\'s questions.'}
        </div>
      </div>

      {conflicts.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 48,
          color: '#666',
          background: '#f8f9fa',
          borderRadius: 8
        }}>
          <h3>No conflicts detected</h3>
          <p>All assignment submissions have submissiondata that matches their assignment questions.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {conflicts.map((c) => {
            const studentName = c.first_name && c.last_name
              ? `${c.first_name} ${c.last_name}`
              : c.primary_email || 'Unknown Student';

            return (
              <div
                key={c.submission_id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  border: '2px solid #ff9800',
                  borderRadius: 8,
                  padding: 16,
                  background: '#fff3e0',
                  boxShadow: '0 1px 4px rgba(255,152,0,0.2)',
                  position: 'relative'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: -8,
                  left: 16,
                  background: c.conflict_type === 'definition_mismatch' ? '#9c27b0' : '#ff9800',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  {c.conflict_type === 'count_mismatch' ? 'COUNT MISMATCH' : c.conflict_type === 'id_mismatch' ? 'ID MISMATCH' : 'DEFINITION MISMATCH'}
                </div>

                <div
                  style={{
                    marginRight: 16,
                    marginTop: 2,
                    cursor: 'pointer',
                    transition: 'transform 0.1s',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="View Submission"
                  onClick={() => window.open(`/projects-directory?command=grade&assignmentSubmission=${c.submission_id}`, '_blank')}
                  onMouseOver={e => e.currentTarget.style.transform = 'scale(1.15)'}
                  onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <span role="img" aria-label="assignment" style={{ fontSize: 28, color: '#fbc02d' }}>📝</span>
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4, color: '#222' }}>
                    {c.assignment_title || 'Untitled Assignment'}
                  </div>
                  <div style={{ fontSize: 14, marginBottom: 4, color: '#e65100', fontWeight: 500 }}>
                    {c.conflict_type === 'definition_mismatch'
                      ? 'Question definition differs (label, type, maxPoints, choices, answer, etc.)'
                      : `Assignment: ${c.assignment_question_count} questions · Submission: ${c.submission_question_count} questions`}
                  </div>
                  <div style={{ fontSize: 13, color: '#666', marginBottom: 2 }}>
                    <strong>Student:</strong> {studentName}
                  </div>
                  <div style={{ fontSize: 13, color: '#666', marginBottom: 2 }}>
                    <strong>Project:</strong> {c.project_title || 'N/A'} {c.project_code && `(${c.project_code})`}
                  </div>
                  <div style={{ fontSize: 13, color: '#666', marginBottom: 2 }}>
                    <strong>Submission ID:</strong> {c.submission_id}
                  </div>
                  {c.graderesult && (
                    <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
                      <strong>Grade:</strong> {c.graderesult}
                    </div>
                  )}
                </div>

                <div style={{ textAlign: 'right', minWidth: 200 }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                    Submitted: {c.submitted_at ? new Date(c.submitted_at).toLocaleString() : 'N/A'}
                  </div>
                  <button
                    onClick={() => window.open(`/projects-directory?command=grade&assignmentSubmission=${c.submission_id}`, '_blank')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 4,
                      background: '#1976d2',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    View Submission
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AssignmentSubmissionsAuditor;
